import { Calendar, Clock } from "lucide-react";
import type { ProjectDetail, Project } from "@/lib/api";

/**
 * Persistent strip showing days remaining to submission deadline. Sits at
 * the top of project pages so the deadline is always in peripheral vision.
 *
 * Color rules:
 *   - red   when overdue (negative days)
 *   - red   when 0–2 days remain (the "panic" window)
 *   - orange when 3–7 days remain
 *   - blue  when 8–30 days remain
 *   - muted otherwise
 *
 * Renders nothing if the project has no deadline set or the project is
 * already submitted/won/lost — the bar would just be noise then.
 */
export default function DeadlineBanner({ project }: { project: ProjectDetail | Project }) {
  if (!project.submissionDeadline) return null;
  if (project.status === "submitted" || project.status === "won" || project.status === "lost") {
    return null;
  }

  const deadline = new Date(project.submissionDeadline);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const { label, color, bg, Icon } = describe(days);
  const formatted = deadline.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div
      className="px-8 py-2.5 flex items-center gap-3 border-b text-xs"
      style={{
        backgroundColor: bg,
        borderColor: "rgba(229,231,235,0.06)",
      }}
    >
      <Icon size={14} style={{ color }} />
      <span className="font-semibold" style={{ color }}>
        {label}
      </span>
      <span style={{ color: "#9CA3AF" }}>·</span>
      <span style={{ color: "#9CA3AF" }}>Deadline {formatted}</span>
    </div>
  );
}

function describe(days: number): {
  label: string;
  color: string;
  bg: string;
  Icon: typeof Calendar;
} {
  if (days < 0) {
    return {
      label: `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`,
      color: "#ef4444",
      bg: "rgba(239,68,68,0.06)",
      Icon: Clock,
    };
  }
  if (days === 0) {
    return {
      label: "Due today",
      color: "#ef4444",
      bg: "rgba(239,68,68,0.06)",
      Icon: Clock,
    };
  }
  if (days <= 2) {
    return {
      label: `${days} day${days === 1 ? "" : "s"} to deadline`,
      color: "#ef4444",
      bg: "rgba(239,68,68,0.06)",
      Icon: Clock,
    };
  }
  if (days <= 7) {
    return {
      label: `${days} days to deadline`,
      color: "#F97316",
      bg: "rgba(249,115,22,0.05)",
      Icon: Clock,
    };
  }
  if (days <= 30) {
    return {
      label: `${days} days to deadline`,
      color: "#60A5FA",
      bg: "rgba(96,165,250,0.05)",
      Icon: Calendar,
    };
  }
  return {
    label: `${days} days to deadline`,
    color: "#9CA3AF",
    bg: "rgba(255,255,255,0.02)",
    Icon: Calendar,
  };
}
